// ============================================
// Rebate CSV Transform Storage Constants
// ============================================

/** Azure Blob container for source documents uploaded during CSV transform sessions */
export const REBATE_CSV_TRANSFORM_SOURCE_CONTAINER = "rebate-csv-transform";

/** Azure Blob container for snapshot artifacts (phase outputs) during CSV transform */
export const REBATE_CSV_TRANSFORM_ARTIFACTS_CONTAINER = "rebate-csv-transforms";

export interface DeltaAnalysisResult {
  activeProductGroupsDelta: {
    removed: string[];
    added: string[];
    unchanged: string[];
  };
  invoiceImpact: {
    totalInvoices: number;
    compatibleInvoices: number;
    incompatibleInvoices: number;
    incompatibleDetails: Array<{
      invoiceId: string;
      invoiceDate: string;
      affectedLineItems: number;
      missingProductGroups: string[];
    }>;
  };
  paymentImpact: {
    totalPayments: number;
    affectedAllocations: number;
    preservedAllocations: number;
    affectedAmount: number;
    unallocatableDetails: Array<{
      paymentId: string;
      paymentDate: string;
      paymentAmount: number;
      affectedRules: string[];
      allocationAmount: number;
    }>;
    deletedRules: string[];
  };
  manualAmountImpact: {
    totalManualAmounts: number;
    affectedManualAmounts: number;
    affectedTotal: number;
    manualAmountDetails: Array<{
      manualAmountId: string;
      divisionName: string;
      amount: number;
      date: string;
      description: string | null;
      ruleName: string;
    }>;
    deletedRules: string[];
  };
  calculationImpact: {
    currentEstimate: number;
    newEstimate: number;
    delta: number;
  };
  exclusiveRuleWarning?: {
    hasExclusiveRules: boolean;
    affectedRules: Array<{
      ruleName: string;
      qualifyingIsExclusive: boolean;
      affectedIsExclusive: boolean;
      currentExcludedGroups: string[];
    }>;
    addedProductGroups: string[];
    warningMessage: string;
  };
  // Rules that will be soft-deleted (set inactive) because they have reported data
  reportedDataImpact?: {
    rulesWithReportedData: Array<{
      ruleId: string;
      ruleName: string;
    }>;
  };
  severity: "none" | "minor" | "major" | "destructive";
}

import { z } from "zod";

// ============================================
// Rule Match Types (Version Comparison)
// ============================================

/**
 * Describes a single field-level difference between a matched extracted rule
 * and its previous-version counterpart.
 */
export const RuleMatchDiffSchema = z.object({
  field: z.string(),
  label: z.string(),
  previous: z.string(),
  extracted: z.string(),
});

export type RuleMatchDiff = z.infer<typeof RuleMatchDiffSchema>;

/**
 * A match between an extracted rule (from PDF) and a previous-version rule.
 * Carries confidence, match type, reasoning, and optional field-level diffs.
 */
export const RuleMatchSchema = z.object({
  extractedRuleId: z.string(),
  matchedPreviousRuleId: z.string(),
  confidence: z.number().min(0).max(100),
  matchType: z.enum(["exact", "fuzzy", "ai_suggested"]),
  matchReason: z.string(),
  differences: z.array(RuleMatchDiffSchema).optional(),
});

export type RuleMatch = z.infer<typeof RuleMatchSchema>;

/**
 * CalculationType - Types of rebate calculations stored in RebateCalculatedBreakdown
 * Single source of truth for calculation type values used across frontend and backend
 */
export const CalculationTypeSchema = z.enum([
  "estimate",
  "linear-regression",
  "conservative",
  "exp_weight_avg",
  "historical",
  "reported",
]);

export type CalculationType = z.infer<typeof CalculationTypeSchema>;

/**
 * CalculationType constants for backwards compatibility with enum-style usage
 * Use CalculationType.ESTIMATE instead of "estimate" for better type safety
 */
export const CalculationTypeValues = {
  ESTIMATE: "estimate" as const,
  LINEAR_REGRESSION: "linear-regression" as const,
  CONSERVATIVE: "conservative" as const,
  EXP_WEIGHT_AVG: "exp_weight_avg" as const,
  HISTORICAL: "historical" as const,
  REPORTED: "reported" as const,
} as const;

// Export as CalculationType for backwards compatibility (replaces enum)
export const CalculationType = CalculationTypeValues;

/** Human-readable display names for each calculation type */
export const CalculationTypeDisplayNames: Record<CalculationType, string> = {
  estimate: "Estimate",
  "linear-regression": "Linear Regression",
  conservative: "Seasonality with Dampening Factor",
  exp_weight_avg: "Exponential Weighted Average",
  historical: "Historical",
  reported: "Reported",
};

/** Prediction strategy types — all calculation types except estimate, reported, and historical */
export const PredictionStrategyTypes = CalculationTypeSchema.options.filter(
  (t): t is Exclude<CalculationType, "estimate" | "reported" | "historical"> =>
    t !== CalculationTypeValues.ESTIMATE &&
    t !== CalculationTypeValues.REPORTED &&
    t !== CalculationTypeValues.HISTORICAL,
);

export enum ThresholdType {
  VOLUME = "volume",
  GROWTH = "growth",
}

export enum ProductInclusionType {
  INCLUSIVE = "inclusive",
  EXCLUSIVE = "exclusive",
}

// Threshold type descriptions
export const ThresholdTypeDescriptions = {
  [ThresholdType.GROWTH]:
    "Percentage of baseline (100% = flat, 105% = 5% growth)",
  [ThresholdType.VOLUME]: "Dollar value thresholds or flat volume rebates",
} as const;

export interface RebateRuleProductGroup {
  id: string;
  name: string;
}

export interface RebateRuleTier {
  id: string;
  tierOrder: number;
  thresholdValue: string; // DECIMAL fields returned as strings by Sequelize
  rebateValue: string; // DECIMAL fields returned as strings by Sequelize
}

export interface RebateRuleDivision {
  id: string;
  name: string;
}

export interface RebateRule {
  id: string;
  rebateAgreementId: string;
  name: string;
  isRetroactiveToDollarOne: boolean | null; // null for manual rules
  isManual: boolean;
  isActive: boolean; // false for soft-deleted rules (preserved to keep reported data)
  thresholdType: string | null; // null for manual rules
  qualifyingInclusionType: string | null; // null for manual rules
  affectedInclusionType: string | null; // null for manual rules
  growthBaseline: string | null; // DECIMAL field returned as string by Sequelize
  discountPercentage: string; // DECIMAL field returned as string by Sequelize, "0" = no discount
  notes: string | null; // Optional notes about the rebate rule
  sortOrder: number; // Order in which rules should be displayed and processed
  // Rule-specific date range. If null, uses agreement dates.
  startDate: string | null; // DATEONLY field returned as string (YYYY-MM-DD) by Sequelize
  endDate: string | null; // DATEONLY field returned as string (YYYY-MM-DD) by Sequelize
  qualifyingProductGroups: RebateRuleProductGroup[];
  affectedProductGroups: RebateRuleProductGroup[];
  tiers: RebateRuleTier[];
  divisions: RebateRuleDivision[];
  createdAt: Date;
  updatedAt: Date;
}

interface DateRange {
  startDate: Date;
  endDate: Date;
}
/**
 * The three buckets reports split reported rebate dollars into.
 */
export type RebateCategory =
  | "Redistribution"
  | "Conditional"
  | "Non-Conditional";

/**
 * Frozen snapshot of the rule-shape inputs that drive a breakdown's
 * categorization, stored on `RebateCalculatedBreakdown.metadata.ruleSnapshot`
 * at write time. Reports re-derive the bucket from this instead of the live
 * rule, so historic categorization no longer drifts when a rule's tags or
 * tiers change.
 *
 * Inputs (`thresholdType`/`isManual`/`tierCount`/`firstTierThreshold`) are
 * stored for the conditional dimension so it stays re-derivable if the
 * conditional rules evolve; the primary-bucket dimension stores the resolved
 * `isPrimaryBucket` verdict, which is the thing that must not drift.
 *
 * There is intentionally no version field — presence of a key is the version
 * signal, and readers fall back to live derivation when it (or a needed field)
 * is absent.
 */
export interface RebateRuleSnapshot {
  thresholdType: string | null;
  isManual: boolean;
  tierCount: number;
  firstTierThreshold: number | null;
  isPrimaryBucket: boolean;
  /**
   * Full rule-tag membership (CoreEnumOption IDs) at write time, so the
   * frontend tag filter/grouping reflects what was true when reported rather
   * than the rule's current tags. Frozen alongside the bucketing verdict.
   */
  tagIds: string[];
}

/**
 * FLAT REBATE BREAKDOWN STRUCTURE
 *
 * Atomic rebate breakdown at the rule-division level.
 * Each breakdown represents exactly one database record and eliminates complex aggregation logic.
 */
export interface RebateBreakdown {
  // Core identifiers
  supplierId: string;
  supplierName: string;
  divisionId: string;
  divisionName: string;
  ruleId: string;
  ruleName: string;
  agreementId: string;
  agreementName: string;

  // The rebate amount for this specific rule-division combination
  rebateAmount: number; // Incremental value for this period
  cumulativeRebateAmount?: number; // Cumulative value at breakdown level (from DB)

  // Volume data for rebate percentage calculations
  totalVolume?: number; // Total invoice volume from supplier
  affectedVolume?: number; // Volume that qualifies for rebates

  // Optional rule details (available for generated forecasts, not stored breakdowns)
  qualifyingVolume?: number;
  tierApplied?: number;
  rebatePercentage?: number;
  invoiceCount?: number;
  calculationType: string;
  isDeactivated?: boolean;

  // Frozen categorization signals, derived once from this row's
  // `metadata.ruleSnapshot` at transform time (REPORTED rows only). Undefined
  // for forecast/synthetic rows, where consumers fall back to live rule-shape
  // categorization. `ruleTagIds` is the rule's frozen tag membership.
  category?: RebateCategory;
  isConditional?: boolean;
  ruleTagIds?: string[];
}

/**
 * A single point in time with rebate data
 * Uses flat structure for maximum frontend flexibility
 */
export interface RebatePoint {
  date: Date;
  daysFromStart: number; // Days since the start of the analysis period
  totalRebateAmount: number; // Sum of all breakdowns[].rebateAmount (incremental)
  totalCumulativeRebateAmount: number; // Sum of all breakdowns[].cumulativeRebateAmount (cumulative)
  totalVolume?: number; // Sum of all breakdowns[].totalVolume
  totalAffectedVolume?: number; // Sum of all breakdowns[].affectedVolume
  breakdowns: RebateBreakdown[]; // Flat array of atomic rebate records
}

/**
 * Overall metrics for rebate percentage calculations
 */
export interface OverallRebateMetrics {
  totalRebateAmount: number;
  totalVolume: number; // All line items
  rebateableVolume: number; // Only line items with productGroupId != null
  affectedVolume: number; // N-counted from breakdowns (kept for backwards compat)
  rebatePercentage: number; // rebate / totalVolume * 100
}

/**
 * Unified forecast result with separate estimates and predictions
 * Now uses flat RebateBreakdown structure for maximum flexibility
 */
export interface UnifiedForecastResult {
  forecastPeriod: DateRange & {
    totalDays: number;
    description: string; // e.g., "Agreement Period: Test Agreement" or "Calendar Year 2025"
  };

  // Historical estimate data points (uses flat breakdown structure)
  // Green line - actuals from real invoice data
  estimates: RebatePoint[];

  // Predicted data points by prediction method (uses flat breakdown structure)
  // Purple/Blue lines - predictions (Purple = past, Blue = future)
  predictions: {
    [method: string]: RebatePoint[]; // e.g., 'linear-regression', 'seasonal', etc.
  };

  // Reported values (Black line) - frozen predictions when period closes
  reported: RebatePoint[];

  /**
   * Total purchase volume per (supplier, division, agreement, month), computed
   * from invoice line items — the source of truth for total volume (intentionally
   * NOT stored on rebate breakdown rows, where it would be n-counted per rule).
   *
   * Breakdowns are synthetic rows with `totalVolume` set and a sentinel rule
   * (`All Volume`) since total volume is not rule-attributable. Path selection
   * mirrors rebate forecasts per (supplier, division, agreement):
   * - current-year invoice months → actuals (`estimate`)
   * - current-year data exists → remaining months use a monthly run-rate
   *   (`prediction`) from months that have data
   * - no current-year data → prior-year same-calendar-month volume (`prediction`)
   * Optional for tolerance of older payloads.
   */
  totalVolumeSeries?: RebatePoint[];

  // Overall metrics for YTD actuals and full-year predictions
  overallMetrics?: {
    actual: OverallRebateMetrics;
    predicted: OverallRebateMetrics;
  };

  // Rule metadata for conditional/non-conditional categorization
  rules: Array<{
    id: string;
    name: string;
    thresholdType: string | null;
    isManual: boolean;
    isActive: boolean;
    agreementId: string;
    isConditional: boolean;
    // Live redistribution verdict, resolved from the org's primaryBucketingTagId
    // setting (falls back to the /redist/i name match). Optional for tolerance
    // of older payloads.
    isPrimaryBucket?: boolean;
    tags?: Array<{ id: string; name: string }>;
  }>;

  // Agreement metadata with tags
  agreements: Array<{
    id: string;
    name: string;
    supplierId: string;
    supplierName: string;
    tags?: Array<{ id: string; name: string }>;
  }>;

  // Supplier metadata with tags
  suppliers: Array<{
    id: string;
    name: string;
    code?: string | null; // from company.metadata.rexel_code
    tags?: Array<{ id: string; name: string }>;
  }>;

  // Division metadata
  divisions: Array<{
    id: string;
    name: string;
    code?: string | null; // from division.metadata.internal_code
  }>;

  // Which prediction algorithm was used to generate REPORTED values
  // Defaults to 'linear-regression' if not set in org config
  reportedAlgorithm: string;

  // Metadata
  forecastDates: Date[]; // All the dates that were analyzed

  // User-set per-(agreement, month) forecast overrides applied to predictions
  // (estimates stay the real actuals). `excluded_zero` masks the month to $0;
  // `risk_factor` scales it by (1 - value/100). Months without an entry follow
  // the default behavior.
  monthOverrides: Array<{
    agreementId: string;
    month: string; // YYYY-MM
    status: "excluded_zero" | "risk_factor";
    // risk_factor percent: positive = reduction (100 ≡ excluded), negative =
    // uplift (e.g. -50 → +50%). null for excluded_zero.
    value: number | null;
  }>;
}

export interface RebateTierProximity {
  supplierId: string;
  supplierName: string;
  supplierTags?: Array<{ id: string; name: string }>;
  agreementId: string;
  agreementName: string;
  ruleId: string;
  ruleName: string;
  thresholdType: string | null; // 'growth' or 'volume' or null for manual rules
  isRetroactiveToDollarOne: boolean | null; // true/false for automatic rules, null for manual
  currentTier: number | null; // null if no tier reached
  currentTierRebatePercentage: number | null; // rebate percentage for current tier
  nextTier: number | null; // null if at highest tier
  nextTierRebatePercentage: number | null; // rebate percentage for next tier
  nextTierThreshold: number | null;
  currentVolume: number;
  volumeNeeded: number | null; // null if at highest tier
  percentageComplete: number; // 0-100, how close to next tier
  currentRebateAmount: number | null; // rebate amount at current volume and tier
  nextTierRebateAmount: number | null; // rebate amount if next tier threshold is reached
  rebateDelta: number | null; // difference between next tier rebate and current rebate
  qualifyingProductGroups: Array<{ id: string; name: string }>;
  affectedProductGroups: Array<{ id: string; name: string }>;
  error?: string; // Error message if tier calculation failed (e.g., missing growth baseline)
}

// ============================================
// Rebate Transaction Record Overlap Detection Types
// ============================================

/**
 * Represents a rebate transaction record that overlaps with the user's new submission date range
 * Used in the overlap detection flow to show users what existing data conflicts
 */
export interface OverlappingRebateTransactionRecord {
  id: string;
  invoiceNumber: string;
  filename: string;
  sheetName?: string;
  dateRange: { start: string; end: string };
  lineItemCount: number;
  totalValue: number;
  submittedAt: string;
}

/**
 * Response from the check-overlap endpoint
 * Tells the frontend whether there are conflicts and what they are
 */
export interface CheckOverlapResponse {
  hasOverlap: boolean;
  overlappingTransactionRecords: OverlappingRebateTransactionRecord[];
  totalAffectedLineItems: number;
}

/**
 * User's choice for how to handle overlapping data
 */
export const OVERLAP_RESOLUTIONS = {
  OVERRIDE: "override",
  KEEP_BOTH: "keep_both",
  PARTIAL_OVERRIDE: "partial_override",
} as const;

export const OVERLAP_RESOLUTION_VALUES = Object.values(OVERLAP_RESOLUTIONS);

export type OverlapResolution =
  (typeof OVERLAP_RESOLUTIONS)[keyof typeof OVERLAP_RESOLUTIONS];

// ============================================
// Supplier Invoice Line Items Types
// Used by the supplier reported amounts page
// ============================================

/**
 * Association between a line item and a rebate rule
 */
export interface SupplierLineItemRuleAssociation {
  agreementId: string;
  agreementName: string;
  ruleId: string;
  ruleName: string;
  isQualifying: boolean;
  isAffected: boolean;
}

/**
 * Tag associated with a product group
 */
export interface ProductGroupTag {
  id: string;
  name: string;
}

/**
 * An enriched invoice line item with product group, tags, and rule associations
 */
export interface EnrichedSupplierLineItem {
  id: string;
  date: string;
  invoiceId: string;
  invoiceNumber: string;
  divisionId: string;
  divisionName: string | null;
  productGroupId: string | null;
  productGroupName: string | null;
  productGroupTags: ProductGroupTag[];
  value: number;
  currency: string;
  isHistorical: boolean;
  isActive: boolean;
  ruleAssociations: SupplierLineItemRuleAssociation[];
}

/**
 * File information for rebate transaction record documents
 */
export interface RebateTransactionRecordFileInfo {
  id: string;
  name: string;
  mimeType?: string;
  fileSizeBytes?: number;
}

/**
 * Summary of a rebate transaction record's rebateable/non-rebateable breakdown
 */
export interface SupplierRebateTransactionRecordSummary {
  invoiceId: string;
  invoiceNumber: string;
  date: string;
  totalVolume: number;
  originalTotalVolume?: number; // Total including inactive (deactivated) line items
  rebateableVolume: number;
  nonRebateableVolume: number;
  lineItemCount: number;
  lineItemDateRange?: {
    earliest: string;
    latest: string;
  } | null;
  files?: {
    originalFiles: RebateTransactionRecordFileInfo[];
    transformedFile: RebateTransactionRecordFileInfo | null;
  };
  createdAt?: string;
}

/**
 * Summary statistics for supplier invoice line items
 */
export interface SupplierLineItemsSummary {
  totalLineItems: number;
  totalVolume: number;
  rebateableVolume: number;
  nonRebateableVolume: number;
  uniqueInvoices: number;
  uniqueAgreements: number;
  uniqueRules: number;
}

/**
 * Pagination info for supplier invoice line items
 */
export interface SupplierLineItemsPageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  limit: number;
  offset: number;
}

/**
 * Full response from the supplier rebate transaction record line items API
 */
export interface SupplierRebateTransactionRecordLineItemsResponse {
  lineItems: EnrichedSupplierLineItem[];
  invoiceSummaries: SupplierRebateTransactionRecordSummary[];
  summary: SupplierLineItemsSummary;
  pageInfo: SupplierLineItemsPageInfo;
}

/**
 * Query filters for the supplier rebate transaction record line items API
 */
export interface SupplierRebateTransactionRecordLineItemsFilters {
  startDate?: string;
  endDate?: string;
  divisionIds?: string[];
  invoiceNumber?: string | string[];
  rebateableOnly?: boolean;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}

export * from "./rebates/dataExport";
export * from "./rebates/csvTransformWorkflow";
